import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Calendar,
  BarChart3,
  Settings,
  Bell,
  Search,
  LogOut,
  Image,
  TrendingUp,
  Users,
  MessageSquare,
  Heart,
  Share2,
  Eye
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuthStore } from '../../store/authStore';

interface ClientData {
  id: string;
  company_name: string;
  email: string;
  phone: string;
}

interface PostData {
  id: string;
  platform: string;
  content: string;
  status: string;
  scheduled_for: string | null;
  created_at: string;
  media_urls: string[];
  campaign_id: string | null;
}

interface AnalyticsData {
  impressions: number;
  reach: number;
  engagement: number;
  likes: number;
  comments: number;
  shares: number;
}

interface CampaignData {
  id: string;
  name: string;
  status: string;
  start_date: string;
  end_date: string;
}

export default function ClientDashboard() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuthStore();
  const [clientData, setClientData] = useState<ClientData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadClientData();
    const cleanup = subscribeToUpdates();
    return cleanup;
  }, [user]);

  const loadClientData = async () => {
    if (!user) {
      console.log('No user found, redirecting to login');
      setLoading(false);
      navigate('/login');
      return;
    }

    try {
      console.log('Loading client data for user:', user.id, user.email);

      const { data, error } = await supabase
        .from('clients')
        .select('id, company_name, email, phone')
        .eq('user_id', user.id)
        .maybeSingle();

      console.log('Client query result:', { data, error });

      if (error) {
        console.error('Error loading client data:', error);
        alert(`Error loading client data: ${error.message}. Check the console for details.`);
        setLoading(false);
        return;
      }

      if (!data) {
        console.log('No client record found for user_id:', user.id);
        console.log('User might not have completed onboarding. Redirecting...');
        setTimeout(() => navigate('/onboarding'), 2000);
        return;
      }

      console.log('Client data loaded successfully:', data);
      setClientData(data);
      setLoading(false);
    } catch (error: any) {
      console.error('Unexpected error loading client data:', error);
      alert(`Unexpected error: ${error?.message || 'Unknown error'}`);
      setLoading(false);
    }
  };

  const subscribeToUpdates = () => {
    if (!user) return () => {};

    const channel = supabase
      .channel('client-updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'clients',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          if (payload.new) {
            setClientData(payload.new as ClientData);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/login');
  };

  const navigation = [
    { name: 'Dashboard', href: '/client', icon: LayoutDashboard },
    { name: 'Content', href: '/client/content', icon: Image },
    { name: 'Calendar', href: '/client/calendar', icon: Calendar },
    { name: 'Analytics', href: '/client/analytics', icon: BarChart3 },
    { name: 'Settings', href: '/client/settings', icon: Settings }
  ];

  if (loading && !clientData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading your dashboard...</p>
          {user && (
            <p className="mt-2 text-sm text-gray-500">
              Logged in as: {user.email}
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!clientData) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center max-w-md bg-white p-8 rounded-lg shadow-sm border border-gray-200">
          <div className="mb-4">
            <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto">
              <svg className="w-8 h-8 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">No Client Profile Found</h2>
          <p className="text-gray-600 mb-4">
            We couldn't find a client profile associated with your account.
          </p>
          {user && (
            <p className="text-sm text-gray-500 mb-4">
              User ID: <code className="bg-gray-100 px-2 py-1 rounded">{user.id}</code>
            </p>
          )}
          <p className="text-sm text-gray-500">
            Redirecting to onboarding in a moment...
          </p>
          <div className="mt-4">
            <button
              onClick={() => navigate('/onboarding')}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Onboarding Now
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <aside className="w-64 bg-white border-r border-gray-200 shadow-sm">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">{clientData?.company_name}</h2>
          <p className="text-sm text-gray-500 mt-1">Client Portal</p>
        </div>

        <nav className="mt-6 px-3">
          {navigation.map((item) => {
            const isActive = location.pathname === item.href;
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`group flex items-center px-3 py-2.5 text-sm font-medium rounded-lg mb-1 transition-all ${
                  isActive
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                }`}
              >
                <item.icon
                  className={`mr-3 h-5 w-5 flex-shrink-0 ${
                    isActive ? 'text-blue-600' : 'text-gray-400 group-hover:text-gray-600'
                  }`}
                />
                {item.name}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-64 p-4 border-t border-gray-200">
          <button
            onClick={handleSignOut}
            className="w-full flex items-center px-3 py-2 text-sm text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-600 transition-colors"
          >
            <LogOut className="mr-3 h-5 w-5" />
            Sign Out
          </button>
        </div>
      </aside>

      <main className="flex-1 overflow-y-auto">
        <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm sticky top-0 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Search className="text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search content, campaigns..."
                className="border-0 focus:ring-0 text-sm"
              />
            </div>

            <div className="flex items-center space-x-4">
              <button className="relative p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
              </button>
              <div className="flex items-center space-x-3">
                <div className="w-9 h-9 bg-blue-600 rounded-full flex items-center justify-center">
                  <span className="text-white text-sm font-bold">
                    {clientData?.company_name?.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-900">{clientData?.company_name}</p>
                  <p className="text-xs text-gray-500">Client</p>
                </div>
              </div>
            </div>
          </div>
        </header>

        <div className="p-6">
          <Routes>
            <Route index element={<ClientOverview clientId={clientData?.id || ''} />} />
          </Routes>
        </div>
      </main>
    </div>
  );
}

function ClientOverview({ clientId }: { clientId: string }) {
  const [posts, setPosts] = useState<PostData[]>([]);
  const [campaigns, setCampaigns] = useState<CampaignData[]>([]);
  const [analytics, setAnalytics] = useState<AnalyticsData>({
    impressions: 0,
    reach: 0,
    engagement: 0,
    likes: 0,
    comments: 0,
    shares: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (clientId) {
      setLoading(true);
      loadDashboardData();
      const cleanup = subscribeToRealTimeUpdates();
      return cleanup;
    }
  }, [clientId]);

  const loadDashboardData = async () => {
    try {
      const [postsResult, campaignsResult] = await Promise.all([
        supabase
          .from('social_media_posts')
          .select('*')
          .eq('client_id', clientId)
          .order('created_at', { ascending: false })
          .limit(10),
        supabase
          .from('social_media_campaigns')
          .select('*')
          .eq('client_id', clientId)
          .eq('status', 'active')
          .order('start_date', { ascending: false })
          .limit(5)
      ]);

      if (postsResult.data) {
        setPosts(postsResult.data);

        if (postsResult.data.length > 0) {
          const postIds = postsResult.data.map(p => p.id);
          const { data: analyticsData } = await supabase
            .from('post_analytics')
            .select('impressions, reach, engagement, likes, comments, shares')
            .in('post_id', postIds);

          if (analyticsData && analyticsData.length > 0) {
            const totals = analyticsData.reduce((acc, curr) => ({
              impressions: acc.impressions + (curr.impressions || 0),
              reach: acc.reach + (curr.reach || 0),
              engagement: acc.engagement + (curr.engagement || 0),
              likes: acc.likes + (curr.likes || 0),
              comments: acc.comments + (curr.comments || 0),
              shares: acc.shares + (curr.shares || 0)
            }), {
              impressions: 0,
              reach: 0,
              engagement: 0,
              likes: 0,
              comments: 0,
              shares: 0
            });
            setAnalytics(totals);
          }
        }
      }

      if (campaignsResult.data) setCampaigns(campaignsResult.data);
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const subscribeToRealTimeUpdates = () => {
    if (!clientId) return () => {};

    const postsChannel = supabase
      .channel(`posts-changes-${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_media_posts',
          filter: `client_id=eq.${clientId}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setPosts(prev => [payload.new as PostData, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setPosts(prev => prev.map(p => p.id === payload.new.id ? payload.new as PostData : p));
          } else if (payload.eventType === 'DELETE') {
            setPosts(prev => prev.filter(p => p.id !== payload.old.id));
          }
        }
      )
      .subscribe();

    const campaignsChannel = supabase
      .channel(`campaigns-changes-${clientId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'social_media_campaigns',
          filter: `client_id=eq.${clientId}`
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            setCampaigns(prev => [payload.new as CampaignData, ...prev]);
          } else if (payload.eventType === 'UPDATE') {
            setCampaigns(prev => prev.map(c => c.id === payload.new.id ? payload.new as CampaignData : c));
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(postsChannel);
      supabase.removeChannel(campaignsChannel);
    };
  };

  const stats = [
    { name: 'Total Posts', value: posts.length, icon: Image, color: 'bg-blue-500' },
    { name: 'Active Campaigns', value: campaigns.filter(c => c.status === 'active').length, icon: TrendingUp, color: 'bg-green-500' },
    { name: 'Total Impressions', value: analytics.impressions.toLocaleString(), icon: Eye, color: 'bg-purple-500' },
    { name: 'Engagement Rate', value: posts.length > 0 ? `${((analytics.engagement / analytics.reach) * 100 || 0).toFixed(1)}%` : '0%', icon: Heart, color: 'bg-pink-500' }
  ];

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      pending_review: 'bg-yellow-100 text-yellow-800',
      approved: 'bg-green-100 text-green-800',
      scheduled: 'bg-blue-100 text-blue-800',
      published: 'bg-indigo-100 text-indigo-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPlatformColor = (platform: string) => {
    const colors: Record<string, string> = {
      instagram: 'bg-gradient-to-br from-purple-500 to-pink-500',
      facebook: 'bg-blue-600',
      linkedin: 'bg-blue-700',
      twitter: 'bg-sky-500',
      tiktok: 'bg-black'
    };
    return colors[platform.toLowerCase()] || 'bg-gray-600';
  };

  if (loading) {
    return (
      <div>
        <div className="mb-8">
          <div className="h-8 w-64 bg-gray-200 rounded animate-pulse mb-2"></div>
          <div className="h-4 w-96 bg-gray-200 rounded animate-pulse"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-2"></div>
              <div className="h-8 w-16 bg-gray-200 rounded animate-pulse"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900">Dashboard Overview</h1>
        <p className="text-gray-600 mt-1">Welcome back! Here's what's happening with your social media.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {stats.map((stat) => (
          <div key={stat.name} className="bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stat.name}</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{stat.value}</p>
              </div>
              <div className={`${stat.color} p-3 rounded-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Posts</h3>
          {posts.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No posts yet. We'll create content for you soon!</p>
          ) : (
            <div className="space-y-4">
              {posts.slice(0, 5).map((post) => (
                <div key={post.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className={`${getPlatformColor(post.platform)} w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0`}>
                    <Image className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 capitalize">{post.platform}</p>
                    <p className="text-sm text-gray-500 truncate">{post.content}</p>
                    <div className="flex items-center space-x-2 mt-1">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getStatusColor(post.status)}`}>
                        {post.status.replace('_', ' ')}
                      </span>
                      {post.scheduled_for && (
                        <span className="text-xs text-gray-500">
                          {new Date(post.scheduled_for).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Active Campaigns</h3>
          {campaigns.length === 0 ? (
            <p className="text-gray-500 text-center py-8">No campaigns running. We'll set one up for you!</p>
          ) : (
            <div className="space-y-4">
              {campaigns.filter(c => c.status === 'active').slice(0, 5).map((campaign) => (
                <div key={campaign.id} className="p-4 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-gray-900">{campaign.name}</h4>
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Active
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {new Date(campaign.start_date).toLocaleDateString()} - {new Date(campaign.end_date).toLocaleDateString()}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {analytics.reach > 0 && (
        <div className="mt-8 bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Performance Snapshot</h3>
          <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
            <div className="text-center">
              <Eye className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{analytics.impressions.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Impressions</p>
            </div>
            <div className="text-center">
              <Users className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{analytics.reach.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Reach</p>
            </div>
            <div className="text-center">
              <Heart className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{analytics.likes.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Likes</p>
            </div>
            <div className="text-center">
              <MessageSquare className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{analytics.comments.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Comments</p>
            </div>
            <div className="text-center">
              <Share2 className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{analytics.shares.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Shares</p>
            </div>
            <div className="text-center">
              <TrendingUp className="h-6 w-6 text-gray-400 mx-auto mb-2" />
              <p className="text-2xl font-bold text-gray-900">{analytics.engagement.toLocaleString()}</p>
              <p className="text-xs text-gray-500">Engagement</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
